import { FC, useEffect, useMemo, useState } from "react";
import { DashboardDevice } from "../../../../../../models/api/DashboardDevice";
import {
  ConfigState,
  HistoricalWidgetMeasurementConfig,
  INITIAL_CONFIG_STATE,
} from "../../../SimpleAppTemplate";
import { ChartCard } from "../../../../../../components/cards";
import { ErrorNoData } from "../../index";
import MeasurementHelpers from "../../../../../../helpers/MeasurementHelpers";
import { useHistoricalChartsFilter } from "../../context/historical-charts-filter";
import { LoadingSkeleton } from "../../../../../../components/LoadingSkeleton";
import { AMCLineTemperature } from "../../../../../../components/charts";
import useHistoricalLineAndTemperature from "../hooks/useHistoricalLineAndTemperature";
import { Guid } from "js-guid";
import SupportLink from "../../../../../../helpers/SupportLink";

type Props = {
  device: DashboardDevice;
  orgId?: string;
  chartLabel: string;
  measurements: HistoricalWidgetMeasurementConfig[];
};

const LineTemperature: FC<Props> = ({ device, orgId, chartLabel, measurements }) => {
  const icons: { icon: string | JSX.Element; errorIcon: string | JSX.Element } = {
    icon: "",
    errorIcon: "fa-regular fa-chart-column",
  };
  const { duration, resolution, timeLastRefresh, customEndDate } = useHistoricalChartsFilter();
  const [lineLabel, setLineLabel] = useState<string>("Line Series");
  const [temperatureLabel, setTemperatureLabel] = useState<string>("Temperature");
  const [configError, setConfigError] = useState<ConfigState>(INITIAL_CONFIG_STATE);
  const {
    fetchData,
    data: historicalChartData,
    isLoading,
    hasError,
    errorMessage,
  } = useHistoricalLineAndTemperature(device.id);

  useEffect(() => {
    if (!!measurements && measurements.length == 2) {
      // Line Data - Required
      const lineName = measurements[0].name;
      const lineFieldKey = measurements[0].fieldKey;

      // Line Data - Optional
      const lineDisplayName = measurements[0].displayName;
      const lineFieldName = measurements[0].fieldName;
      const linePortNumber = parseInt(measurements[0].port ?? "0");
      const lineLabel = measurements[0].label;

      // Temperature Data - Required
      const temperatureName = measurements[1].name;
      const temperatureFieldKey = measurements[1].fieldKey;

      // Temperature Data - Optional
      const temperatureDisplayName = measurements[0].displayName;
      const temperatureFieldName = measurements[0].fieldName;
      const temperaturePortNumber = parseInt(measurements[1].port ?? "0");
      const temperatureLabel = measurements[1].label;

      const hasConfigError = !lineName || !lineFieldKey || !temperatureName || !temperatureFieldKey;
      if (hasConfigError) {
        setConfigError({
          hasConfigError: true,
          errorMsg: "No Measurement Name and/or FieldKey configured for this widget",
        });
      }

      if (lineLabel) setLineLabel(lineLabel);
      if (temperatureLabel) setTemperatureLabel(temperatureLabel);

      if (!hasConfigError) {
        const getData = async () => {
          await fetchData({
            duration,
            resolution,
            customEndDate,
            hoursToAdd: MeasurementHelpers.getDifferenceInHoursFromUTCTime(),
            lineName,
            lineFieldKey,
            temperatureName,
            temperatureFieldKey,
            lineDisplayName,
            lineFieldName,
            linePortNumber,
            temperatureDisplayName,
            temperatureFieldName,
            temperaturePortNumber,
            orgId,
          });
        };

        getData();
      }
    } else {
      setConfigError({
        hasConfigError: true,
        errorMsg: "We need 2 Measurement Configurations for this widget",
      });

      if (measurements.length > 2) {
        setConfigError({
          hasConfigError: true,
          errorMsg: "Only 2 Measurement Configurations is needed for this widget",
        });
      }
    }
  }, [timeLastRefresh, orgId]);

  const GetDisplayChart = useMemo(() => {
    //If there is config error
    if (configError.hasConfigError) {
      return (
        <ErrorNoData
          cardCaption={`${configError.errorMsg}.`}
          supportQuestion={
            <p>
              Configured correctly?
              <span>&nbsp;</span>
              <SupportLink />
            </p>
          }
          iconClass={icons.errorIcon}
          iconPos="left"
        />
      );
    }

    // If it is still loading
    if (isLoading) {
      return <LoadingSkeleton width="100%" height="500px" />;
    }

    // If there is an error after loading
    if (hasError) {
      return (
        <ErrorNoData
          cardCaption={`${errorMessage}`}
          supportQuestion={
            <p>
              <SupportLink />
            </p>
          }
          iconClass={icons.errorIcon}
          iconPos="left"
        />
      );
    }

    if (historicalChartData.hasData) {
      return (
        <AMCLineTemperature
          data={historicalChartData.historicalData}
          lineLabel={lineLabel}
          temperatureLabel={temperatureLabel}
          chartId={new Guid().toString()}
        />
      );
    } else {
      if (historicalChartData.errorMessage) {
        return (
          <ErrorNoData
            cardCaption={`${historicalChartData.errorMessage}.`}
            supportQuestion={
              <p>
                <SupportLink />
              </p>
            }
            iconClass={icons.errorIcon}
            iconPos="left"
          />
        );
      }

      return (
        <ErrorNoData
          cardCaption={`No data for ${device.name} in this duration.`}
          supportQuestion={
            <p>
              Expecting data?
              <span>&nbsp;</span>
              <SupportLink />
            </p>
          }
          iconClass={icons.errorIcon}
          iconPos="left"
        />
      );
    }
  }, [historicalChartData.historicalData?.length, configError]);

  return (
    <ChartCard
      chartTitle={chartLabel == "" ? "Line x Temperature" : chartLabel}
      showFilters={true}
      chart={<>{GetDisplayChart}</>}
    />
  );
};

export default LineTemperature;
